import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, Sparkles, UserPlus, Eye, CalendarCheck,
  Camera, Music, Palette, UtensilsCrossed, PartyPopper,
  Scissors, Flower2, Star, Users, Gift,
  CheckCircle, Clock, TrendingUp, Shield
} from 'lucide-react';

const PROVIDER_TYPES = [
  { label: 'Balloon Artists', icon: PartyPopper, color: 'text-pink-500', bg: 'bg-pink-50' },
  { label: 'Backdrop & Decor', icon: Palette, color: 'text-purple-500', bg: 'bg-purple-50' },
  { label: 'DJs', icon: Music, color: 'text-blue-500', bg: 'bg-blue-50' },
  { label: 'Photographers', icon: Camera, color: 'text-amber-500', bg: 'bg-amber-50' },
  { label: 'Caterers', icon: UtensilsCrossed, color: 'text-orange-500', bg: 'bg-orange-50' },
  { label: 'Event Planners', icon: CalendarCheck, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  { label: 'Party Rentals', icon: Users, color: 'text-teal-500', bg: 'bg-teal-50' },
  { label: 'Face Painters', icon: Star, color: 'text-yellow-500', bg: 'bg-yellow-50' },
  { label: 'Makeup Artists', icon: Scissors, color: 'text-rose-500', bg: 'bg-rose-50' },
  { label: 'Florists', icon: Flower2, color: 'text-green-500', bg: 'bg-green-50' },
];

const STEPS = [
  {
    num: '1',
    title: 'Create your profile',
    desc: 'Add your services, photos, pricing, and availability. Takes about 5 minutes.',
  },
  {
    num: '2',
    title: 'Get discovered locally',
    desc: 'Customers searching for services in your area find your profile and reach out.',
  },
  {
    num: '3',
    title: 'Accept bookings & grow',
    desc: 'Respond to inquiries, send quotes, accept deposits, and build your reputation.',
  },
];

const BENEFITS = [
  { icon: Eye, title: 'Local visibility', desc: 'Show up when people near you search for your services.' },
  { icon: CalendarCheck, title: 'Easier booking flow', desc: 'Manage inquiries, quotes, and payments in one place.' },
  { icon: Clock, title: 'Get in early', desc: 'Founding providers get priority placement and fee-free months.' },
  { icon: TrendingUp, title: 'One profile, ongoing leads', desc: 'Build it once. Get found again and again.' },
];

export default function FoundingProvidersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleCTA = () => {
    // Preserve any ref/utm params for tracking
    const ref = searchParams.get('ref');
    const utm = searchParams.get('utm_source');
    let target = '/auth?role=vendor&mode=signup';
    if (ref) target += `&ref=${encodeURIComponent(ref)}`;
    if (utm) target += `&utm_source=${encodeURIComponent(utm)}`;
    navigate(target);
  };

  const scrollToHow = () => {
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="founding-providers-page">
      {/* Minimal top bar */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 h-14">
          <button onClick={() => navigate('/')} className="flex items-center gap-1.5" data-testid="fp-logo">
            <Sparkles className="h-5 w-5 text-emerald-600" />
            <span className="font-bold text-lg text-gray-900">Celey</span>
          </button>
          <Button
            size="sm"
            fullWidth={false}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-4 text-sm"
            onClick={handleCTA}
            data-testid="fp-header-cta"
          >
            Join Free
          </Button>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="pt-12 pb-14 sm:pt-20 sm:pb-20 px-4 bg-gradient-to-b from-emerald-50/80 to-white">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 text-xs font-semibold px-3 py-1 rounded-full mb-5">
            <Gift className="h-3.5 w-3.5" />
            Now onboarding founding providers
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-4" data-testid="fp-hero-heading">
            Get found. Get booked.
            <br />
            <span className="text-emerald-600">Grow your event business.</span>
          </h1>

          <p className="text-base sm:text-lg text-gray-600 mb-8 max-w-xl mx-auto leading-relaxed">
            Celey connects local event service providers with customers planning birthdays, weddings, baby showers, graduations, and other celebrations — all in one place.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-base px-7 py-6 rounded-full shadow-lg shadow-emerald-600/20"
              onClick={handleCTA}
              data-testid="fp-hero-primary-cta"
            >
              Join as a Provider <ArrowRight className="ml-1.5 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-gray-200 text-gray-700 hover:bg-gray-50 text-base px-7 py-6 rounded-full"
              onClick={scrollToHow}
              data-testid="fp-hero-secondary-cta"
            >
              See How It Works
            </Button>
          </div>

          <p className="text-xs text-gray-400 mt-5">
            Free to join. No monthly fees. No contracts.
          </p>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="py-14 sm:py-20 px-4 bg-white" data-testid="fp-how-section">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-3">
            How It Works
          </h2>
          <p className="text-gray-500 text-center mb-10">Three steps to your first booking.</p>

          <div className="space-y-6">
            {STEPS.map((s) => (
              <div key={s.num} className="flex gap-4 sm:gap-5">
                <div className="flex-shrink-0 w-10 h-10 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  {s.num}
                </div>
                <div className="pt-1">
                  <h3 className="font-semibold text-gray-900 text-lg mb-0.5">{s.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO IT'S FOR ── */}
      <section className="py-14 sm:py-20 px-4 bg-gray-50" data-testid="fp-who-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-3">
            Built for Event Service Providers
          </h2>
          <p className="text-gray-500 text-center mb-10 max-w-lg mx-auto">
            Whether you run a full business or book gigs on the side — if people hire you for events, Celey is for you.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {PROVIDER_TYPES.map((p) => (
              <div key={p.label} className="flex flex-col items-center gap-2 bg-white rounded-xl p-4 border border-gray-100">
                <div className={`w-10 h-10 rounded-lg ${p.bg} flex items-center justify-center`}>
                  <p.icon className={`h-5 w-5 ${p.color}`} />
                </div>
                <span className="text-xs sm:text-sm font-medium text-gray-700 text-center leading-tight">{p.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOUNDING PROVIDER ── */}
      <section className="py-14 sm:py-20 px-4 bg-white" data-testid="fp-founding-section">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-b from-emerald-50/60 to-white p-6 sm:p-10">
            <div className="flex items-center gap-2 mb-4">
              <div className="bg-emerald-600 p-1.5 rounded-lg">
                <Star className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                Founding Provider Benefits
              </h2>
            </div>

            <p className="text-gray-600 mb-6 leading-relaxed">
              Providers who join now — while we&apos;re still growing in your area — get benefits that won&apos;t be available later.
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">$0 platform fees for your first 3 months</p>
                  <p className="text-sm text-gray-500">Keep every dollar you earn during your free period.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Extend your free period with referrals</p>
                  <p className="text-sm text-gray-500">Refer other providers and earn additional months at $0 fees.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Priority visibility in your area</p>
                  <p className="text-sm text-gray-500">Early providers get seen first as customer demand grows.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Shape the platform</p>
                  <p className="text-sm text-gray-500">Your feedback directly influences what we build next.</p>
                </div>
              </div>
            </div>

            <Button
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-base rounded-full px-7 py-6 w-full sm:w-auto"
              onClick={handleCTA}
              data-testid="fp-founding-cta"
            >
              <UserPlus className="mr-2 h-5 w-5" />
              Claim Your Founding Provider Spot
            </Button>
          </div>
        </div>
      </section>

      {/* ── WHY JOIN ── */}
      <section className="py-14 sm:py-20 px-4 bg-gray-50" data-testid="fp-benefits-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-10">
            Why Join Celey
          </h2>

          <div className="grid sm:grid-cols-2 gap-4">
            {BENEFITS.map((b) => (
              <div key={b.title} className="flex items-start gap-4 bg-white p-5 rounded-xl border border-gray-100">
                <div className="bg-emerald-50 p-2.5 rounded-lg flex-shrink-0">
                  <b.icon className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-0.5">{b.title}</h3>
                  <p className="text-sm text-gray-500 leading-snug">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-16 sm:py-20 px-4 bg-emerald-600" data-testid="fp-final-cta-section">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
            Ready to get your first booking through Celey?
          </h2>
          <p className="text-emerald-100 mb-8 leading-relaxed">
            Create your free profile in minutes. Start showing up when local customers search for event services.
          </p>
          <Button
            size="lg"
            className="bg-white text-emerald-600 hover:bg-emerald-50 text-base px-8 py-6 rounded-full font-semibold shadow-lg"
            onClick={handleCTA}
            data-testid="fp-final-cta-btn"
          >
            Join as a Provider — It&apos;s Free <ArrowRight className="ml-1.5 h-5 w-5" />
          </Button>
          <p className="text-xs text-emerald-200/80 mt-5">
            No credit card required. No monthly fees. No commitments.
          </p>
        </div>
      </section>

      {/* ── APP NOTE ── */}
      <section className="py-8 px-4 bg-white border-t border-gray-100">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-sm text-gray-500">
            <Shield className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />
            Celey works on any device. Open it in your phone&apos;s browser — no app download needed.
          </p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-gray-900 text-white py-10">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-5 w-5 text-emerald-400" />
            <span className="text-xl font-bold">Celey</span>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-6 text-sm">
            <button onClick={() => navigate('/')} className="text-gray-400 hover:text-white transition-colors">Home</button>
            <button onClick={() => navigate('/browse')} className="text-gray-400 hover:text-white transition-colors">Browse</button>
            <button onClick={() => navigate('/contact')} className="text-gray-400 hover:text-white transition-colors">Support</button>
            <button onClick={() => navigate('/faqs')} className="text-gray-400 hover:text-white transition-colors">FAQs</button>
          </div>
          <div className="text-gray-500 text-xs">
            © {new Date().getFullYear()} Celey. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
